import React from "react";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";

/**
 * The summary strip above the tiles: the handful of numbers that previously
 * required adding up a list by eye.
 *
 * ONE ROW, DELIBERATELY. It sits between the tiles and the top of the page, so
 * every pixel it takes comes off the VS panel — which is the thing the owner
 * has repeatedly asked for more of. Five stats, one line, no card chrome.
 *
 * A stat renders only when it has something to say. A client with no invoices
 * shows no money stats rather than a row of zeroes, because "$0.00 lifetime"
 * reads as a verdict on the client and "nothing here yet" is the truth.
 */

const Stat = ({ label, value, tone, title }) => (
  <div className="scc-summary-stat" title={title}>
    <span className="scc-summary-label">{label}</span>
    <span
      className={["scc-summary-value", tone ? `is-${tone}` : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {value}
    </span>
  </div>
);

const ClientConsoleSummary = ({ summary }) => {
  if (!summary) return null;

  const {
    lifetimeCents,
    outstandingCents,
    unpaidCount,
    lastJobMs,
    nextJobMs,
    quotesTotal,
    quotesWon,
    winRate,
  } = summary;

  const hasLifetime = Number(lifetimeCents) > 0;
  const hasOutstanding = Number(outstandingCents) > 0;
  const hasAny =
    hasLifetime || hasOutstanding || lastJobMs || nextJobMs || winRate !== null;

  if (!hasAny) return null;

  return (
    <div className="scc-summary" aria-label="Client summary">
      {hasLifetime && (
        <Stat
          label="Lifetime"
          value={priceStrFormatter(lifetimeCents)}
          title="Total of every invoice marked paid"
        />
      )}

      {hasOutstanding && (
        <Stat
          label="Outstanding"
          tone="owed"
          value={priceStrFormatter(outstandingCents)}
          title={`${unpaidCount} unpaid invoice${unpaidCount === 1 ? "" : "s"}`}
        />
      )}

      {lastJobMs && (
        <Stat
          label="Last job"
          value={BAGroupTimeFormat(String(lastJobMs), false, true, true)}
          title="Most recent invoiced job"
        />
      )}

      {/* Only when there IS one. An empty "Next job —" is a row of nothing. */}
      {nextJobMs && (
        <Stat
          label="Next job"
          tone="upcoming"
          value={BAGroupTimeFormat(String(nextJobMs), false, true, true)}
          title="Nearest scheduled job still ahead"
        />
      )}

      {winRate !== null && (
        <Stat
          label="Quotes won"
          value={`${winRate}%`}
          title={`${quotesWon} of ${quotesTotal} decided quotes became invoices. Quotes still active are not counted either way.`}
        />
      )}
    </div>
  );
};

export default ClientConsoleSummary;
